/**
 * Protection Tap — Guide de l'administrateur (Français)
 */
const content = `# Protection Tap — Guide de l'administrateur

**Où trouver ces paramètres** : Modale **Paramètres** de chaque carte → section *Protection Tap*.
**Où voir l'exécution en temps réel** : **Super Admin → Moniteur de santé des Taps** (\`/admin/tap-health\`).

---

## En 30 secondes

Protection Tap décide si le tap NFC d'un client est comptabilisé ou filtré comme suspect.

* **Protection Tap ACTIVÉE** — la plateforme applique les contrôles anti-fraude. Seuls les taps « propres » sont comptés.
* **Protection Tap DÉSACTIVÉE** — chaque tap légitime est compté, sans filtrage. À choisir pour des analyses brutes sans protection.

Sous le commutateur principal, deux sous-protections indépendantes peuvent être activées ou désactivées :

| Sous-protection | Bloque | S'applique à |
|---|---|---|
| **Protection contre les doublons** | Les taps répétés du même téléphone dans une courte fenêtre | Toutes les cartes |
| **Protection Durée minimale** | Les taps où le client n'est pas resté assez longtemps sur la page | Cartes Landing Page et Review Shield uniquement |

---

## Comment activer la Protection Tap

1. Ouvrez **Cartes** dans la barre latérale.
2. Trouvez la carte et cliquez sur son bouton **Paramètres**.
3. Descendez à la section **Protection Tap**.
4. Basculez **Protection Tap** sur **ACTIVÉE**.
5. Cliquez sur **Enregistrer**.

Une fois activée, les deux sous-protections apparaissent en dessous, **activées par défaut**.

## Comment désactiver la Protection Tap

1. Ouvrez **Cartes → Paramètres** pour la carte.
2. Basculez l'interrupteur principal **Protection Tap** sur **DÉSACTIVÉE**.
3. Cliquez sur **Enregistrer**.

Lorsqu'elle est désactivée, chaque tap légitime est compté. Les règles Duplicate et Dwell sont ignorées.

## Configurer la Protection contre les doublons

Empêche le même téléphone d'être compté plus d'une fois dans la fenêtre choisie.

1. Avec **Protection Tap** activée, regardez la sous-section **Protection contre les doublons**.
2. Activez-la (par défaut) ou désactivez-la.
3. Réglez la **Fenêtre (secondes)** — le délai entre deux taps du même appareil.
   * \`30 s\` (par défaut) — usage général.
   * \`60 s\` — restaurants à comptoirs à forte rotation où l'associé et le client peuvent taper ensemble.
   * \`15 s\` — kiosques à fort volume où presque chaque tap est un client distinct.
4. Cliquez sur **Enregistrer**.

## Configurer la Protection Durée minimale

Exige que le client passe un temps minimal sur votre Landing Page ou Review Shield avant que le tap soit comptabilisé.

1. Avec **Protection Tap** activée, regardez la sous-section **Protection Durée minimale**.
2. Activez-la (par défaut) ou désactivez-la.
3. Réglez le **Seuil (secondes)** — la durée minimale.
   * \`10 s\` (par défaut) — la plupart des entreprises.
   * \`20 s\` — si vous avez une longue vidéo d'intro et souhaitez un engagement réel.
   * \`5 s\` — contenu de landing très court, flux de vente rapide.
4. Cliquez sur **Enregistrer**.

> Sur une carte **Direct Tap** (sans landing page), la Protection Durée minimale est affichée **désactivée** avec un avis ambre. C'est intentionnel — vous ne pouvez pas l'activer car il n'y a aucune durée à mesurer. Passer la carte en mode Landing Page réactive automatiquement le bouton.

---

## Différences entre Direct Tap et Landing Page

| Aspect | Direct Tap | Landing Page |
|---|---|---|
| Ce que le client voit en tapant | Redirection immédiate vers Google Reviews | Votre page brandée d'abord, puis redirection |
| Protection contre les doublons | S'applique | S'applique |
| Protection Durée minimale | Non applicable | S'applique |
| Rigueur anti-fraude | Doublons uniquement | Doublons + Durée |
| Idéal pour | Conversion rapide et sans friction | Branding, incitations, promotions multi-plateformes |

## Comportement de Review Shield

Review Shield est une landing page particulière : le client doit franchir une porte de satisfaction avant d'être dirigé vers Google (satisfait) ou vers votre formulaire de feedback privé (insatisfait). Du point de vue de la Protection Tap, Review Shield est traité **exactement comme Landing Page** :

* Protection contre les doublons s'applique.
* Protection Durée minimale s'applique.
* Les deux se configurent indépendamment.

---

## Interpréter les résultats du Moniteur de santé

La page **Moniteur de santé des Taps** (\`/admin/tap-health\`) reflète exactement ce que l'exécution applique. Ouvrez le *Diagnostic en un clic* d'une carte pour voir :

**Protection Tap : Activée / Désactivée**
* Activée → au moins une sous-protection peut s'exécuter.
* Désactivée → rien n'est filtré.

**Protection contre les doublons :**
* *Activée · fenêtre de 30 s* — le module est actif, bloque les répétitions à 30 s.
* *Désactivée* — le module est désactivé (le maître peut encore être actif).
* *Désactivée (Protection Tap désactivée)* — l'interrupteur maître est off.

**Protection Durée minimale :**
* *Activée · seuil de 10 s* — le module s'exécute pour cette carte.
* *Non applicable* — carte Direct Tap ; aucune page à mesurer.
* *Désactivée* — le module a été désactivé manuellement.

La liste **Suppressions récentes** montre chaque tap rejeté récemment avec sa raison lisible (« Tap en double » ou « Durée minimale non atteinte »). Si une carte se plaint de taps manquants, c'est le premier endroit à regarder.

## Dépanner les taps rejetés

**Symptôme — « Mes taps ne sont pas comptés »**

1. Ouvrez \`/admin/tap-health\` et saisissez l'identifiant de la carte dans le *Diagnostic en un clic*.
2. Lisez le badge **Verdict** en haut.

**Verdict : sain** — la Protection Tap est correcte. Le problème est probablement ailleurs. Vérifiez que :
* Le client tape bien la bonne carte.
* La carte n'est pas en statut \`désactivée\`.
* L'URL de redirection pointe où attendu.

**Verdict : silencieuse** — la carte n'a enregistré aucun tap en 7 jours. Vérifiez si la carte est bien tapée (physique / logistique) ou si la redirection est cassée.

**Verdict : silencieuse-avec-suppressions** — la carte *est* tapée, mais chaque tap est rejeté. Regardez la liste *Suppressions récentes* :
* Beaucoup de **Tap en double** → votre fenêtre est trop généreuse ; réduisez-la, ou désactivez temporairement.
* Beaucoup de **Durée minimale non atteinte** → les clients quittent la page trop vite ; réduisez le seuil ou activez la redirection automatique.

**Verdict : échecs-d-écriture** — la plateforme n'arrive pas à persister les taps. Remontez à l'équipe technique avec les horodatages et types d'erreur affichés.

---

## Questions fréquentes

**Puis-je désactiver une protection et conserver l'autre ?**
Oui. Doublons et Durée sont totalement indépendants.

**Si je désactive la Protection Tap, vais-je perdre l'historique ?**
Non. Le commutateur n'affecte que les taps futurs. L'historique est intact.

**Pourquoi la Protection Durée minimale est-elle grisée sur certaines cartes ?**
Parce que ces cartes sont en mode **Direct Tap** sans landing page — il n'y a aucune page à mesurer. Passer en mode Landing Page la débloque.

**La Protection Tap affecte-t-elle Google Reviews ?**
Non. Elle décide seulement si la plateforme *compte* un tap dans les analyses/incitations. La redirection du client vers Google se fait de toute façon.

**Comment tester ma nouvelle configuration ?**
1. Tapez la carte vous-même une fois.
2. Ouvrez immédiatement \`/admin/tap-health\` → Diagnostiquer → votre card_id.
3. Vous devriez voir le tap dans *Taps (24h)*. Tapez à nouveau rapidement — le second tap devrait soit compter (Doublons off) soit apparaître dans *Suppressions récentes* comme « Tap en double » (Doublons on).
`;

export default content;
